// services/v3/monitoring.ts — thin Sentry façade (SPEC §1).
// Every function is a safe no-op when Sentry was never initialised
// (plugins/sentry.client.ts only inits when a dsn + environment are configured).
import * as Sentry from '@sentry/vue'
import type { QrToken } from '~/types/v3/token'

function enabled(): boolean {
  try {
    return !!Sentry.getClient()
  } catch {
    return false
  }
}

export interface SessionContextExtra {
  dataSource?: string
  writeMode?: string
}

/**
 * Attach (or clear, when `token` is null) the QR session context to every
 * subsequent Sentry event.
 */
export function setSessionContext(token: QrToken | null, extra?: SessionContextExtra): void {
  if (!enabled()) return
  try {
    if (!token) {
      Sentry.setUser(null)
      Sentry.setTag('storeId', undefined)
      Sentry.setTag('table', undefined)
      Sentry.setTag('qrcodeId', undefined)
      Sentry.setTag('dataSource', undefined)
      Sentry.setTag('writeMode', undefined)
      return
    }
    Sentry.setUser({ id: token.storeId })
    Sentry.setTag('storeId', token.storeId)
    Sentry.setTag('table', token.table)
    Sentry.setTag('qrcodeId', token.qrcodeId)
    if (extra?.dataSource !== undefined) Sentry.setTag('dataSource', extra.dataSource)
    if (extra?.writeMode !== undefined) Sentry.setTag('writeMode', extra.writeMode)
  } catch {
    /* never let monitoring break the app */
  }
}

export function captureError(err: unknown, extra?: Record<string, unknown>): void {
  if (!enabled()) return
  try {
    Sentry.captureException(err, extra ? { extra } : undefined)
  } catch {
    /* ignore */
  }
}

export function addBreadcrumb(message: string, data?: Record<string, unknown>): void {
  if (!enabled()) return
  try {
    Sentry.addBreadcrumb({ message, data, level: 'info' })
  } catch {
    /* ignore */
  }
}
